/**
 * Render-error boundary — catches exceptions thrown during render or
 * lifecycle inside the wrapped subtree and renders a visible fallback
 * instead of the empty document body that React produces on an
 * unhandled error.
 *
 * Used as a safety net around pages whose post-success branches have
 * historically white-screened (notably the module-quiz "reviewing"
 * branch after a passing submit). The fallback shows a clickable
 * "Reload" so the user can recover without losing the underlying
 * server-side state (the quiz pass IS persisted; the UI just lost it).
 */
import * as React from "react";

export interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** Optional label shown in the fallback ("the module quiz", etc.). */
  label?: string;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error("ErrorBoundary caught:", error, info.componentStack);
  }

  override render(): React.ReactNode {
    if (this.state.error === null) return this.props.children;
    const label = this.props.label ?? "this page";
    return (
      <section
        className="mx-auto my-12 max-w-2xl rounded-lg border border-red-200 bg-red-50 p-6 text-sm text-red-900"
        role="alert"
        data-testid="error-boundary-fallback"
      >
        <h2 className="text-base font-semibold">Something went wrong rendering {label}.</h2>
        <p className="mt-2">
          Your progress is saved on the server — reloading this page should
          recover the view. If it keeps happening, course home will let you
          continue from where you left off.
        </p>
        <p className="mt-2 font-mono text-xs text-red-800">
          {this.state.error.message}
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-md bg-red-700 px-4 py-2 text-xs font-semibold text-white hover:bg-red-800"
          >
            Reload page
          </button>
          <a
            href="/course"
            className="rounded-md border border-red-300 px-4 py-2 text-xs font-semibold text-red-900 hover:bg-red-100"
          >
            Back to Course home
          </a>
        </div>
      </section>
    );
  }
}
